import { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { isUserLoggedIn } from '../../../utils/loginHelper';

/**
 * Custom error thrown when an API request is blocked due to an expired session token.
 */
export class SessionExpiredError extends Error implements Partial<AxiosError> {
    readonly isAxiosError = true;

    readonly response = {
        status: 403,
        statusText: 'Session Expired',
        data: {
            message: 'Session expired. Please login again.',
        },
        headers: {},
        config: {} as AxiosRequestConfig,
    };

    constructor() {
        super('Session expired. Please login again.');
        this.name = 'SessionExpiredError';
        Object.setPrototypeOf(this, SessionExpiredError.prototype);
    }
}

/**
 * Axios request interceptor that blocks requests when the user's session token has expired.
 */
export function tokenExpirationInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use(
        (config) => {
            // Skip token check for auth endpoints that don't require a token
            const url = config.url?.toLowerCase() ?? '';
            const isAuthEndpoint = url.includes('/token') || url.includes('/login') || url.includes('/oauth');

            if (isAuthEndpoint) {
                return config;
            }

            // Skip token check on internal login page — pre-auth API calls (e.g. company list) should not be blocked
            const isOnInternalLoginPage = window.location.pathname.toLowerCase().endsWith('/internallogin');
            if (isOnInternalLoginPage) {
                return config;
            }

            // Only check if the user was previously logged in
            const wasLoggedIn = localStorage.getItem('is_logged_in');
            if (!wasLoggedIn) {
                return config;
            }

            // If token is expired, block the request
            if (!isUserLoggedIn()) {
                throw new SessionExpiredError();
            }

            return config;
        },
        (error) => Promise.reject(error)
    );
}
